import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { Benchmark, BenchmarkCreate, BenchmarkUpdate } from '@/modules/multichannel/types/configuracion'

interface BenchmarkFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  benchmark: Benchmark | null
  onSubmit: (data: BenchmarkCreate) => void
  onUpdate: (id: number, data: BenchmarkUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  categoria: string
  tasa_entrega: string
  tasa_aperturas: string
  tasa_clics: string
  descripcion: string
}

export function BenchmarkFormDialog({
  open, onOpenChange, benchmark, onSubmit, onUpdate, isSubmitting,
}: BenchmarkFormDialogProps) {
  const isEdit = !!benchmark

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { categoria: '', tasa_entrega: '', tasa_aperturas: '', tasa_clics: '', descripcion: '' },
  })

  useEffect(() => {
    if (open && benchmark) {
      reset({
        categoria: benchmark.categoria,
        tasa_entrega: String(benchmark.tasa_entrega),
        tasa_aperturas: String(benchmark.tasa_aperturas),
        tasa_clics: String(benchmark.tasa_clics),
        descripcion: benchmark.descripcion || '',
      })
    } else if (!open) {
      reset({ categoria: '', tasa_entrega: '', tasa_aperturas: '', tasa_clics: '', descripcion: '' })
    }
  }, [open, benchmark, reset])

  const onFormSubmit = (data: FormData) => {
    const payload = {
      categoria: data.categoria,
      tasa_entrega: Number(data.tasa_entrega),
      tasa_aperturas: Number(data.tasa_aperturas),
      tasa_clics: Number(data.tasa_clics),
      descripcion: data.descripcion || undefined,
    }
    if (isEdit && benchmark) {
      onUpdate(benchmark.id, payload)
    } else {
      onSubmit(payload)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Benchmark' : 'Nuevo Benchmark'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifica las tasas de referencia.' : 'Define las tasas de referencia para una categoria.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="categoria">Categoria <span className="text-destructive">*</span></Label>
            <Input id="categoria" {...register('categoria', { required: 'Requerido' })} disabled={isSubmitting} autoFocus />
            {errors.categoria && <p className="text-sm text-destructive">{errors.categoria.message}</p>}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tasa_entrega">Tasa Entrega %</Label>
              <Input id="tasa_entrega" type="number" step="0.01" {...register('tasa_entrega', { required: 'Requerido', min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} disabled={isSubmitting} />
              {errors.tasa_entrega && <p className="text-sm text-destructive">{errors.tasa_entrega.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tasa_aperturas">Tasa Aperturas %</Label>
              <Input id="tasa_aperturas" type="number" step="0.01" {...register('tasa_aperturas', { required: 'Requerido', min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} disabled={isSubmitting} />
              {errors.tasa_aperturas && <p className="text-sm text-destructive">{errors.tasa_aperturas.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tasa_clics">Tasa Clics %</Label>
              <Input id="tasa_clics" type="number" step="0.01" {...register('tasa_clics', { required: 'Requerido', min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} disabled={isSubmitting} />
              {errors.tasa_clics && <p className="text-sm text-destructive">{errors.tasa_clics.message}</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <Input id="descripcion" {...register('descripcion')} disabled={isSubmitting} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
